import { useMemo, useState } from 'react';

import { AlertTriangle, ExternalLink, Loader2, Play, Plus, RotateCw, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  useCallAgentRuns,
  useRunClassifier,
  useRunQuality,
  useUpdateClassificationLabels,
  useUpdateQualityFlags,
} from '@/hooks/useCalls';
import { useClassifierConfigList } from '@/hooks/useClassifierConfigs';
import { useQualityConfigList } from '@/hooks/useQualityConfigs';
import { cn } from '@/lib/utils';

import { JudgeCorrectionDialog } from './JudgeCorrectionDialog';
import { JudgeTuningInlineStatus } from './JudgeTuningInlineStatus';

import type { FeedbackTargetType } from '@/lib/api/judge-tuning.api';
import type { CallWithContact } from '@repo/shared-types';

interface Props {
  call: CallWithContact;
}

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const isLongString = (v: unknown) => typeof v === 'string' && v.length > 40;
const isScalar = (v: unknown) =>
  typeof v === 'number' || typeof v === 'string' || v === null || v === undefined;

/** Booleans rendered as compact key+chip grid. Clickable when onToggle supplied. */
function BooleanGrid({
  entries,
  onToggle,
  disabled,
}: {
  entries: [string, boolean][];
  onToggle?: (key: string, next: boolean) => void;
  disabled?: boolean;
}) {
  if (entries.length === 0) return null;
  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
      {entries.map(([key, value]) => {
        const chipClass = value
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
          : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
        return (
          <div key={key} className="flex items-center justify-between gap-2 text-xs">
            <span className="text-muted-foreground truncate font-mono">{key}</span>
            {onToggle ? (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onToggle(key, !value)}
                title={disabled ? 'Saving…' : 'Click to toggle'}
                className={cn(
                  'cursor-pointer rounded-full border px-2 py-px text-[10px] leading-4 font-medium transition-colors',
                  chipClass,
                  'hover:opacity-80',
                  disabled && 'cursor-wait opacity-60',
                )}
              >
                {value ? 'true' : 'false'}
              </button>
            ) : (
              <Badge variant="outline" className={chipClass}>
                {value ? 'true' : 'false'}
              </Badge>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ScalarTable({ entries }: { entries: [string, unknown][] }) {
  if (entries.length === 0) return null;
  return (
    <table className="w-full table-fixed text-xs">
      <tbody>
        {entries.map(([key, value]) => {
          const longValue = isLongString(value);
          return (
            <tr key={key} className="border-b last:border-b-0">
              <td className="text-muted-foreground w-1/3 py-1.5 pr-3 align-top font-mono break-words">
                {key}
              </td>
              <td className="py-1.5 align-top break-words">
                {value === null || value === undefined ? (
                  <span className="text-muted-foreground italic">—</span>
                ) : longValue ? (
                  <span className="leading-relaxed whitespace-pre-wrap">{String(value)}</span>
                ) : (
                  <span className="font-mono font-medium">{formatScalar(value)}</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const formatScalar = (v: unknown): string => {
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
};

function partition(record: Record<string, unknown> | undefined): {
  booleans: [string, boolean][];
  scalars: [string, unknown][];
  objects: [string, unknown][];
} {
  if (!record) return { booleans: [], scalars: [], objects: [] };
  const booleans: [string, boolean][] = [];
  const scalars: [string, unknown][] = [];
  const objects: [string, unknown][] = [];
  for (const [k, v] of Object.entries(record)) {
    if (typeof v === 'boolean') booleans.push([k, v]);
    else if (isScalar(v)) scalars.push([k, v]);
    else objects.push([k, v]);
  }
  return { booleans, scalars, objects };
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
        {label}
      </div>
      {children}
    </div>
  );
}

function RecordView({
  record,
  onToggleBoolean,
  toggleDisabled,
}: {
  record: Record<string, unknown> | undefined;
  onToggleBoolean?: (key: string, next: boolean) => void;
  toggleDisabled?: boolean;
}) {
  if (!record || Object.keys(record).length === 0) {
    return <p className="text-muted-foreground text-xs italic">empty</p>;
  }
  const { booleans, scalars, objects } = partition(record);
  return (
    <div className="space-y-3">
      {booleans.length > 0 && (
        <BooleanGrid entries={booleans} onToggle={onToggleBoolean} disabled={toggleDisabled} />
      )}
      {scalars.length > 0 && <ScalarTable entries={scalars} />}
      {objects.map(([k, v]) => (
        <details key={k} className="rounded-md border bg-gray-50/50 dark:bg-gray-900/30">
          <summary className="text-muted-foreground cursor-pointer px-2 py-1 font-mono text-xs">
            {k}
          </summary>
          <pre className="overflow-x-auto px-2 pb-2 text-[10px]">{JSON.stringify(v, null, 2)}</pre>
        </details>
      ))}
    </div>
  );
}

// ─── Tab strip ──────────────────────────────────────────────────────────────

function TabStrip({
  tabs,
  activeName,
  onSelect,
  onNew,
}: {
  tabs: { name: string; configLabel?: string }[];
  activeName: string | '__new__' | null;
  onSelect: (name: string) => void;
  onNew: () => void;
}) {
  return (
    <div className="-mx-1 flex flex-wrap items-center gap-1 border-b pb-2">
      {tabs.map((t) => {
        const isActive = t.name === activeName;
        return (
          <button
            key={t.name}
            type="button"
            onClick={() => onSelect(t.name)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              isActive
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
            title={t.configLabel}
          >
            {t.name}
          </button>
        );
      })}
      <button
        type="button"
        onClick={onNew}
        className={cn(
          'ml-1 inline-flex items-center gap-1 rounded-md border border-dashed px-2 py-1 text-xs font-medium transition-colors',
          activeName === '__new__'
            ? 'border-foreground text-foreground'
            : 'text-muted-foreground hover:border-foreground hover:text-foreground',
        )}
      >
        <Plus className="h-3 w-3" />
        New run
      </button>
    </div>
  );
}

// ─── "New run" form ─────────────────────────────────────────────────────────

function NewRunForm({
  configOptions,
  existingNames,
  configPlaceholder,
  onCancel,
  onRun,
  isRunning,
  defaultName,
}: {
  configOptions: { id: string; name: string }[];
  existingNames: string[];
  configPlaceholder: string;
  onCancel: () => void;
  onRun: (opts: { name: string; configId: string }) => void;
  isRunning: boolean;
  defaultName: string;
}) {
  const [name, setName] = useState('');
  const [configId, setConfigId] = useState('');

  const trimmedName = name.trim();
  const nameTaken = trimmedName.length > 0 && existingNames.includes(trimmedName);
  const canSubmit = !!trimmedName && !!configId && !nameTaken && !isRunning;

  return (
    <div className="space-y-3 rounded-md border bg-gray-50/40 p-3 text-xs dark:bg-gray-900/30">
      <div className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
        New named run
      </div>
      <div className="space-y-2">
        <div>
          <label className="text-muted-foreground mb-1 block text-[11px]">
            Name{' '}
            <span className="text-muted-foreground/70">
              (distinct per call, lowercase id-style)
            </span>
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={defaultName}
            className="h-7 text-xs"
            maxLength={50}
          />
          {nameTaken && (
            <p className="text-destructive mt-1 text-[11px]">
              A run named &quot;{trimmedName}&quot; already exists — pick another name or re-run
              that tab.
            </p>
          )}
        </div>
        <div>
          <label className="text-muted-foreground mb-1 block text-[11px]">Config</label>
          <select
            value={configId}
            onChange={(e) => setConfigId(e.target.value)}
            className="bg-background h-7 w-full rounded-md border px-2 text-xs"
          >
            <option value="">{configPlaceholder}</option>
            {configOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={isRunning}>
          <X className="mr-1 h-3 w-3" />
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => onRun({ name: trimmedName, configId })}
          disabled={!canSubmit}
        >
          {isRunning ? (
            <>
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              Running…
            </>
          ) : (
            <>
              <Play className="mr-1 h-3 w-3" />
              Run
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ─── Classifier card ────────────────────────────────────────────────────────

type ClassifierRun = NonNullable<
  ReturnType<typeof useCallAgentRuns>['data']
>['classifications'][number];

function ClassifierCard({
  callId,
  runs,
  isLoading,
}: {
  callId: string;
  runs: ClassifierRun[];
  isLoading: boolean;
}) {
  const [activeName, setActiveName] = useState<string | '__new__' | null>(null);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const classifier = useRunClassifier(callId);
  const updateLabels = useUpdateClassificationLabels(callId);
  const { data: configs } = useClassifierConfigList();

  // Default-select the canonical run, then the first available, then 'new' if empty.
  const effectiveActive: string | '__new__' = useMemo(() => {
    if (activeName !== null) return activeName;
    if (runs.length === 0) return '__new__';
    const canonical = runs.find((r) => r.name === 'classification');
    return (canonical ?? runs[0]).name;
  }, [activeName, runs]);

  const activeRun =
    effectiveActive !== '__new__' ? runs.find((r) => r.name === effectiveActive) : undefined;

  const toggleLabel = (key: string, next: boolean) => {
    if (!activeRun) return;
    const merged: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(activeRun.labels)) {
      if (typeof v === 'boolean') merged[k] = v;
    }
    merged[key] = next;
    updateLabels.mutate({ labels: merged, name: activeRun.name });
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <CardTitle>Classification</CardTitle>
            <CardDescription>
              {runs.length === 0
                ? 'No classification yet — run the ClassifierAgent on this call.'
                : activeRun
                  ? `${activeRun.classifier_config?.name ?? 'unknown config'} · ${
                      activeRun.taxonomy_version
                    } · ${formatDate(activeRun.classified_at) ?? 'recently'}`
                  : 'Start a new named run below.'}
            </CardDescription>
          </div>
          {activeRun && (
            <div className="flex shrink-0 gap-1">
              <Button size="sm" variant="ghost" asChild>
                <Link
                  to={`/classifier-executions/${callId}/${encodeURIComponent(activeRun.name)}`}
                  title="Full execution details (incl. exact model input)"
                >
                  <ExternalLink className="mr-1 h-3 w-3" />
                  Details
                </Link>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  classifier.mutate({
                    classifierConfigId: activeRun.classifier_config_id ?? undefined,
                    name: activeRun.name,
                  })
                }
                // A null config means an inline `agent` workflow node wrote this
                // row; there is no config to re-run it with. Re-running would fall
                // back to the default classifier config and overwrite the row with
                // unrelated output — re-run the workflow instead.
                disabled={classifier.isPending || !activeRun.classifier_config_id}
                title={
                  activeRun.classifier_config_id
                    ? undefined
                    : 'Written by an agent workflow node — re-run that workflow instead.'
                }
              >
                {classifier.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Running…
                  </>
                ) : (
                  <>
                    <RotateCw className="mr-2 h-3 w-3" />
                    Re-run
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && runs.length === 0 ? (
          <p className="text-muted-foreground text-xs italic">Loading…</p>
        ) : (
          <>
            <TabStrip
              tabs={runs.map((r) => ({
                name: r.name,
                configLabel: r.classifier_config?.name,
              }))}
              activeName={effectiveActive}
              onSelect={(n) => setActiveName(n)}
              onNew={() => setActiveName('__new__')}
            />

            {effectiveActive === '__new__' ? (
              <NewRunForm
                configOptions={configs ?? []}
                existingNames={runs.map((r) => r.name)}
                configPlaceholder="Pick a classifier config…"
                onCancel={() => setActiveName(runs[0]?.name ?? null)}
                isRunning={classifier.isPending}
                defaultName="classification"
                onRun={async ({ name, configId }) => {
                  await classifier.mutateAsync({ classifierConfigId: configId, name });
                  setActiveName(name);
                }}
              />
            ) : activeRun ? (
              <>
                <Section label="Labels">
                  <RecordView
                    record={activeRun.labels}
                    onToggleBoolean={toggleLabel}
                    toggleDisabled={updateLabels.isPending}
                  />
                </Section>
                {Object.keys(activeRun.data).length > 0 && (
                  <Section label="Data">
                    <RecordView record={activeRun.data} />
                  </Section>
                )}
                {updateLabels.isError && (
                  <p className="text-destructive text-xs">
                    {updateLabels.error instanceof Error
                      ? updateLabels.error.message
                      : 'Failed to save labels'}
                  </p>
                )}
              </>
            ) : (
              <p className="text-muted-foreground text-xs italic">Pick a tab or start a new run.</p>
            )}
          </>
        )}
        {classifier.isError && (
          <p className="text-destructive text-xs">
            {classifier.error instanceof Error ? classifier.error.message : 'Run failed'}
          </p>
        )}

        {activeRun && activeRun.classifier_config_id && (
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground mt-2 h-7 text-xs"
            onClick={() => setCorrectionOpen(true)}
          >
            <AlertTriangle className="mr-1 h-3 w-3" />
            Judge got this wrong
          </Button>
        )}

        <JudgeTuningInlineStatus callId={callId} targetType="classifier" />

        {activeRun && activeRun.classifier_config_id && (
          <JudgeCorrectionDialog
            open={correctionOpen}
            onOpenChange={setCorrectionOpen}
            callId={callId}
            targetType={'classifier' as FeedbackTargetType}
            configId={activeRun.classifier_config_id}
            configName={activeRun.classifier_config?.name ?? 'unknown'}
            actualLabels={Object.fromEntries(
              Object.entries(activeRun.labels).filter(
                (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
              ),
            )}
            actualData={Object.fromEntries(
              Object.entries(activeRun.data).filter(([, v]) => typeof v !== 'boolean'),
            )}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Quality card ───────────────────────────────────────────────────────────

type QualityRun = NonNullable<ReturnType<typeof useCallAgentRuns>['data']>['qualities'][number];

function QualityCard({
  callId,
  runs,
  isLoading,
}: {
  callId: string;
  runs: QualityRun[];
  isLoading: boolean;
}) {
  const [activeName, setActiveName] = useState<string | '__new__' | null>(null);
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const quality = useRunQuality(callId);
  const updateFlags = useUpdateQualityFlags(callId);
  const { data: configs } = useQualityConfigList();

  const effectiveActive: string | '__new__' = useMemo(() => {
    if (activeName !== null) return activeName;
    if (runs.length === 0) return '__new__';
    const canonical = runs.find((r) => r.name === 'quality');
    return (canonical ?? runs[0]).name;
  }, [activeName, runs]);

  const activeRun =
    effectiveActive !== '__new__' ? runs.find((r) => r.name === effectiveActive) : undefined;

  const toggleFlag = (key: string, next: boolean) => {
    if (!activeRun) return;
    const merged: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(activeRun.flags)) {
      if (typeof v === 'boolean') merged[k] = v;
    }
    merged[key] = next;
    updateFlags.mutate({ flags: merged, name: activeRun.name });
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <CardTitle>Quality</CardTitle>
            <CardDescription>
              {runs.length === 0
                ? 'No quality assessment yet — run the QualityAgent on this call.'
                : activeRun
                  ? `${activeRun.quality_config?.name ?? 'unknown config'} · ${
                      activeRun.rubric_version
                    } · ${formatDate(activeRun.assessed_at) ?? 'recently'}`
                  : 'Start a new named run below.'}
            </CardDescription>
          </div>
          {activeRun && (
            <div className="flex shrink-0 gap-1">
              <Button size="sm" variant="ghost" asChild>
                <Link
                  to={`/quality-executions/${callId}/${encodeURIComponent(activeRun.name)}`}
                  title="Full execution details (incl. exact model input)"
                >
                  <ExternalLink className="mr-1 h-3 w-3" />
                  Details
                </Link>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  quality.mutate({
                    qualityConfigId: activeRun.quality_config_id ?? undefined,
                    name: activeRun.name,
                  })
                }
                // See the classifier Re-run above: a null config means an inline
                // `agent` node wrote this row, so there is nothing to re-run with.
                disabled={quality.isPending || !activeRun.quality_config_id}
                title={
                  activeRun.quality_config_id
                    ? undefined
                    : 'Written by an agent workflow node — re-run that workflow instead.'
                }
              >
                {quality.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Running…
                  </>
                ) : (
                  <>
                    <RotateCw className="mr-2 h-3 w-3" />
                    Re-run
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && runs.length === 0 ? (
          <p className="text-muted-foreground text-xs italic">Loading…</p>
        ) : (
          <>
            <TabStrip
              tabs={runs.map((r) => ({
                name: r.name,
                configLabel: r.quality_config?.name,
              }))}
              activeName={effectiveActive}
              onSelect={(n) => setActiveName(n)}
              onNew={() => setActiveName('__new__')}
            />

            {effectiveActive === '__new__' ? (
              <NewRunForm
                configOptions={configs ?? []}
                existingNames={runs.map((r) => r.name)}
                configPlaceholder="Pick a quality config…"
                onCancel={() => setActiveName(runs[0]?.name ?? null)}
                isRunning={quality.isPending}
                defaultName="quality"
                onRun={async ({ name, configId }) => {
                  await quality.mutateAsync({ qualityConfigId: configId, name });
                  setActiveName(name);
                }}
              />
            ) : activeRun ? (
              <>
                <Section label="Flags">
                  <RecordView
                    record={activeRun.flags}
                    onToggleBoolean={toggleFlag}
                    toggleDisabled={updateFlags.isPending}
                  />
                </Section>
                {Object.keys(activeRun.data).length > 0 && (
                  <Section label="Data">
                    <RecordView record={activeRun.data} />
                  </Section>
                )}
                {activeRun.reasoning && (
                  <Section label="Reasoning">
                    <p className="text-xs leading-relaxed whitespace-pre-wrap">
                      {activeRun.reasoning}
                    </p>
                  </Section>
                )}
                {updateFlags.isError && (
                  <p className="text-destructive text-xs">
                    {updateFlags.error instanceof Error
                      ? updateFlags.error.message
                      : 'Failed to save flags'}
                  </p>
                )}
              </>
            ) : (
              <p className="text-muted-foreground text-xs italic">Pick a tab or start a new run.</p>
            )}
          </>
        )}
        {quality.isError && (
          <p className="text-destructive text-xs">
            {quality.error instanceof Error ? quality.error.message : 'Run failed'}
          </p>
        )}

        {activeRun && activeRun.quality_config_id && (
          <Button
            size="sm"
            variant="ghost"
            className="text-muted-foreground mt-2 h-7 text-xs"
            onClick={() => setCorrectionOpen(true)}
          >
            <AlertTriangle className="mr-1 h-3 w-3" />
            Judge got this wrong
          </Button>
        )}

        <JudgeTuningInlineStatus callId={callId} targetType="quality" />

        {activeRun && activeRun.quality_config_id && (
          <JudgeCorrectionDialog
            open={correctionOpen}
            onOpenChange={setCorrectionOpen}
            callId={callId}
            targetType={'quality' as FeedbackTargetType}
            configId={activeRun.quality_config_id}
            configName={activeRun.quality_config?.name ?? 'unknown'}
            actualLabels={Object.fromEntries(
              Object.entries(activeRun.flags).filter(
                (entry): entry is [string, boolean] => typeof entry[1] === 'boolean',
              ),
            )}
            actualData={Object.fromEntries(
              Object.entries(activeRun.data).filter(([, v]) => typeof v !== 'boolean'),
            )}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Top-level panel ────────────────────────────────────────────────────────

export function AgentResultsPanel({ call }: Props) {
  const { data, isLoading } = useCallAgentRuns(call.id);
  const classifications = data?.classifications ?? [];
  const qualities = data?.qualities ?? [];

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <ClassifierCard callId={call.id} runs={classifications} isLoading={isLoading} />
      <QualityCard callId={call.id} runs={qualities} isLoading={isLoading} />
    </div>
  );
}
